import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft, MapPin, Package, Truck, User, Home, Calendar, AlertTriangle, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManageBookingPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [pickupSlot, setPickupSlot] = useState(null);
  const [deliverySlot, setDeliverySlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Reschedule state
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleType, setRescheduleType] = useState(null); // 'pickup' or 'delivery'
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/details`);
      setBooking(response.data.booking);
      setPickupSlot(response.data.pickup_slot);
      setDeliverySlot(response.data.delivery_slot);
    } catch (error) {
      console.error("Error fetching booking:", error);
      toast.error("Réservation introuvable");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDates = async () => {
    if (!booking) return;
    try {
      const response = await axios.get(`${API}/slots/${booking.zone_id}/dates`);
      setAvailableDates(response.data.map(d => new Date(d)));
    } catch (error) {
      console.error("Error fetching dates:", error);
    }
  };

  const fetchSlots = async (date) => {
    if (!booking) return;
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/slots/${booking.zone_id}?date=${dateStr}`);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };

  const handleStartReschedule = async (type) => {
    setRescheduleType(type);
    setShowReschedule(true);
    setSelectedDate(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
    await fetchAvailableDates();
  };

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    await fetchSlots(date);
  };

  const handleReschedule = async () => {
    if (!selectedSlot) {
      toast.error("Veuillez sélectionner un créneau");
      return;
    }

    setActionLoading(true);
    try {
      const data = rescheduleType === 'pickup' 
        ? { new_pickup_slot_id: selectedSlot.id }
        : { new_delivery_slot_id: selectedSlot.id };
      
      await axios.put(`${API}/bookings/${bookingId}/reschedule`, data);
      toast.success("Réservation modifiée avec succès");
      setShowReschedule(false);
      await fetchBookingDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler cette réservation ?")) {
      return;
    }

    setActionLoading(true);
    try {
      await axios.put(`${API}/bookings/${bookingId}/cancel`);
      toast.success("Réservation annulée");
      await fetchBookingDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setActionLoading(false);
    }
  };

  const isDateAvailable = (date) => {
    return availableDates.some(d => 
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Réservation introuvable</h1>
        <Button onClick={() => navigate("/")} className="btn-primary">
          Retour à l'accueil
        </Button>
      </div>
    );
  }

  const zoneName = booking.zone_id === "lyon" ? "Lyon" : "Saint-Rambert-d'Albon";
  const isCancelled = booking.status === "cancelled";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Gérer ma réservation
        </h1>

        {/* Status Banner */}
        {isCancelled && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <X className="w-5 h-5 text-red-600" />
            <p className="text-red-800 font-medium">Cette réservation a été annulée</p>
          </div>
        )}

        {!isCancelled && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-medium">Modification possible jusqu'à la veille 19h</p>
              <p className="text-amber-700 text-sm">Passé ce délai, contactez-nous directement.</p>
            </div>
          </div>
        )}

        {/* Booking Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Détails de la réservation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Zone */}
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <MapPin className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm text-slate-500">Zone</p>
                <p className="font-medium">{zoneName}</p>
              </div>
            </div>

            {/* Pickup */}
            {pickupSlot && (
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-pink-600" />
                  <div>
                    <p className="text-sm text-slate-500">Ramassage</p>
                    <p className="font-medium">{format(new Date(pickupSlot.date), "EEEE d MMMM", { locale: fr })}</p>
                    <p className="text-sm text-slate-600">{pickupSlot.start_time} - {pickupSlot.end_time}</p>
                  </div>
                </div>
                {!isCancelled && (
                  <Button variant="outline" size="sm" onClick={() => handleStartReschedule('pickup')}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                )}
              </div>
            )}

            {/* Delivery */}
            {deliverySlot && (
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-slate-500">Livraison</p>
                    <p className="font-medium">{format(new Date(deliverySlot.date), "EEEE d MMMM", { locale: fr })}</p>
                    <p className="text-sm text-slate-600">{deliverySlot.start_time} - {deliverySlot.end_time}</p>
                  </div>
                </div>
                {!isCancelled && (
                  <Button variant="outline" size="sm" onClick={() => handleStartReschedule('delivery')}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                )}
              </div>
            )}

            {/* Client Info */}
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <User className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm text-slate-500">Client</p>
                <p className="font-medium">{booking.first_name} {booking.last_name}</p>
                {booking.phone && <p className="text-sm text-slate-600">{booking.phone}</p>}
              </div>
            </div>

            {/* Address */}
            <div className="flex items-center gap-3">
              <Home className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm text-slate-500">Adresse</p>
                <p className="font-medium">{booking.address}</p>
              </div>
            </div>

            {/* Remarks */}
            {booking.remarks && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Remarques / Articles</p>
                <p className="text-slate-900">{booking.remarks}</p>
              </div>
            )}

            {/* Reference */}
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Référence</p>
              <p className="font-mono text-sm">{booking.id}</p>
            </div>
          </CardContent>
        </Card>

        {/* Cancel Button */}
        {!isCancelled && (
          <Button
            variant="outline"
            className="w-full border-red-300 text-red-600 hover:bg-red-50"
            onClick={handleCancel}
            disabled={actionLoading}
          >
            <X className="w-4 h-4 mr-2" />
            Annuler ma réservation
          </Button>
        )}

        {/* Reschedule Modal */}
        {showReschedule && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Modifier le {rescheduleType === 'pickup' ? 'ramassage' : 'livraison'}
                </h2>
                <button onClick={() => setShowReschedule(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-2">Choisir une nouvelle date :</p>
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    locale={fr}
                    disabled={(date) => !isDateAvailable(date)}
                    className="rounded-xl border"
                  />
                </div>

                {selectedDate && (
                  <div>
                    <p className="text-sm font-medium mb-2">Créneaux disponibles :</p>
                    {availableSlots.length === 0 ? (
                      <p className="text-slate-500 text-sm">Aucun créneau disponible</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {availableSlots.map((slot) => {
                          const isFull = slot.current_bookings >= slot.max_bookings;
                          const isSelected = selectedSlot?.id === slot.id;
                          return (
                            <button
                              key={slot.id}
                              onClick={() => !isFull && setSelectedSlot(slot)}
                              disabled={isFull}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                isSelected ? "border-primary bg-primary/10 ring-2 ring-primary/20" :
                                isFull ? "opacity-50 cursor-not-allowed bg-slate-50" :
                                "border-slate-200 hover:border-primary/50"
                              }`}
                            >
                              <p className="font-medium text-sm">{slot.start_time} - {slot.end_time}</p>
                              <p className="text-xs text-slate-500">
                                {isFull ? "Complet" : `${slot.max_bookings - slot.current_bookings} place(s)`}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" className="flex-1" onClick={() => setShowReschedule(false)}>
                    Annuler
                  </Button>
                  <Button 
                    className="flex-1 btn-primary" 
                    onClick={handleReschedule}
                    disabled={!selectedSlot || actionLoading}
                  >
                    {actionLoading ? "..." : "Confirmer"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageBookingPage;
